import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

function classify(op) {
  if (op.type === "eq") return "diff-eq";
  if (op.type === "del" && op.char === " ") return "diff-del bg-yellow-200";
  if (op.type === "ins" && op.char === " ") return "diff-ins bg-yellow-200";
  if (op.type === "del") return "diff-del";
  if (op.type === "ins") return "diff-ins";
  return "";
}

function summarize(ops) {
  const out = { missing: 0, extra: 0, missing_space: 0, extra_space: 0, correct: 0 };
  for (const o of ops) {
    if (o.type === "eq") out.correct++;
    else if (o.type === "del" && o.char === " ") out.missing_space++;
    else if (o.type === "ins" && o.char === " ") out.extra_space++;
    else if (o.type === "del") out.missing++;
    else if (o.type === "ins") out.extra++;
  }
  return out;
}

function DiffView({ ops }) {
  return (
    <div className="font-mono text-sm whitespace-pre-wrap leading-7 break-all">
      {ops.map((op, i) => (
        <span key={`op-${i}-${op.type}-${op.char}`} className={classify(op)}>{op.char === " " ? "·" : op.char}</span>
      ))}
    </div>
  );
}

export default function CandidateResultDetail() {
  const { sid } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const r = await api.get(`/candidate/submissions/${sid}`); setData(r.data); })(); }, [sid]);
  if (!data) return <div className="text-zinc-500">{t("loading")}</div>;

  const totalSummary = data.fields.reduce((acc, f) => {
    const s = summarize(f.ops);
    Object.keys(s).forEach((k) => { acc[k] = (acc[k] || 0) + s[k]; });
    return acc;
  }, {});

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-zinc-500 inline-flex items-center gap-1 mb-4"><ArrowLeft size={14} /> {t("back")}</button>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{data.page_name}</h1>
      <p className="text-zinc-500 mt-2 text-sm">{lang === "hi" ? "विस्तृत त्रुटि विश्लेषण।" : "Detailed error analysis. Spaces shown as ·"}</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
        {[
          [lang === "hi" ? "सही" : "Correct", totalSummary.correct, "text-green-700 border-green-200 bg-green-50"],
          [lang === "hi" ? "गुम वर्ण" : "Missing", totalSummary.missing, "text-red-700 border-red-200 bg-red-50"],
          [lang === "hi" ? "अतिरिक्त वर्ण" : "Extra", totalSummary.extra, "text-rose-700 border-rose-200 bg-rose-50"],
          [lang === "hi" ? "गुम स्पेस" : "Missing Space", totalSummary.missing_space, "text-amber-700 border-amber-200 bg-amber-50"],
          [lang === "hi" ? "अतिरिक्त स्पेस" : "Extra Space", totalSummary.extra_space, "text-amber-700 border-amber-200 bg-amber-50"],
        ].map(([k, v, cls]) => (
          <div key={k} className={`card-clean p-3 sm:p-4 ${cls}`}>
            <div className="text-xs uppercase tracking-widest opacity-80">{k}</div>
            <div className="font-display text-xl sm:text-2xl font-bold mt-1">{v || 0}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {data.fields.map((f) => (
          <div key={f.index} className="card-clean p-5">
            <div className="flex justify-between items-center mb-3">
              <div className="font-display font-semibold">{t("field")} {f.index + 1}</div>
              <span className={`chip ${f.accuracy >= 95 ? "text-green-700 border-green-200" : f.accuracy >= 80 ? "text-amber-700 border-amber-200" : "text-red-700 border-red-200"}`}>{f.accuracy}%</span>
            </div>
            <div className="grid lg:grid-cols-3 gap-4 text-sm">
              <div><div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{t("original")}</div><div className="font-mono leading-7 whitespace-pre-wrap break-all bg-zinc-50 rounded p-3">{f.original}</div></div>
              <div><div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{t("typed")}</div><div className="font-mono leading-7 whitespace-pre-wrap break-all bg-zinc-50 rounded p-3">{f.typed}</div></div>
              <div><div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{t("diff")}</div><div className="bg-zinc-50 rounded p-3"><DiffView ops={f.ops} /></div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
